import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { LOGO_URL } from "@/components/InkCharacter";
import { PolicyGate } from "@/components/PolicyGate";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, needsOnboarding, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import WelcomePage from "@/pages/WelcomePage";
import SignUpPage from "@/pages/SignUpPage";
import SignInPage from "@/pages/SignInPage";
import { ConfirmEmailChangePage, ForgotPasswordPage, LegalPage, ResetPasswordPage, VerifyEmailPage } from "@/pages/AccountPages";
import OnboardingPage from "@/pages/OnboardingPage";
import Dashboard from "@/pages/Dashboard";
import ConsultationPage from "@/pages/ConsultationPage";
import HistoryPage from "@/pages/HistoryPage";
import KitPage from "@/pages/KitPage";
import LibraryPage from "@/pages/LibraryPage";
import AdminPage from "@/pages/AdminPage";
import ProfilePage from "@/pages/ProfilePage";
import NewConsultationPage from "@/pages/NewConsultationPage";
import { ReportPage, ReportsPage } from "@/pages/ReportsPage";
import { BillingPage, PaymentSuccessPage, SubscribePage } from "@/pages/BillingPages";
import PricingPage from "@/pages/PricingPage";

const locked = (u) => u && u.role !== "admin" && !u.billing?.has_access;

function Loading() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#FDFBF7]" data-testid="app-loading" role="status" aria-label="Ink Genius is loading">
      <img src={LOGO_URL} alt="Ink Genius" className="ig-splash h-64 w-auto sm:h-80" />
      <div className="flex gap-1.5" aria-hidden="true">
        {[0, 1, 2].map((i) => <span key={i} className="ig-dot h-1.5 w-1.5 rounded-full bg-[#C5A059]" style={{ animationDelay: `${i * 0.2}s` }} />)}
      </div>
    </div>
  );
}



function Protected({ children, admin, open }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/welcome" replace />;
  if (!open && locked(user)) return <Navigate to="/subscribe" replace />;
  if (needsOnboarding(user) && !(open && locked(user))) return <Navigate to="/onboarding" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}<PolicyGate /></Layout>;
}

function OnboardingRoute() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/welcome" replace />;
  if (locked(user)) return <Navigate to="/subscribe" replace />;
  if (!needsOnboarding(user)) return <Navigate to="/" replace />;
  return <><OnboardingPage /><PolicyGate /></>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to={locked(user) ? "/subscribe" : needsOnboarding(user) ? "/onboarding" : "/"} replace />;
  return children;
}

function WelcomeRoute() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user && !locked(user)) return <Navigate to={needsOnboarding(user) ? "/onboarding" : "/"} replace />;
  return <WelcomePage />;
}

function SignedIn({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/welcome" replace />;
  return <>{children}<PolicyGate /></>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/welcome" element={<WelcomeRoute />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/signup" element={<PublicOnly><SignUpPage /></PublicOnly>} />
            <Route path="/signin" element={<PublicOnly><SignInPage /></PublicOnly>} />
            <Route path="/auth" element={<Navigate to="/signin" replace />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/onboarding" element={<OnboardingRoute />} />
            <Route path="/subscribe" element={<SignedIn><SubscribePage /></SignedIn>} />
            <Route path="/payment/success" element={<SignedIn><PaymentSuccessPage /></SignedIn>} />
            <Route path="/billing" element={<Protected open><BillingPage /></Protected>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/consult/new" element={<Protected><NewConsultationPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/reports/:id" element={<Protected><ReportPage /></Protected>} />
            <Route path="/consult/:id" element={<Protected><ConsultationPage /></Protected>} />
            <Route path="/history" element={<Protected><HistoryPage /></Protected>} />
            <Route path="/kit" element={<Protected><KitPage /></Protected>} />
            <Route path="/library" element={<Protected><LibraryPage /></Protected>} />
            <Route path="/profile" element={<Protected open><ProfilePage /></Protected>} />
            <Route path="/admin" element={<Protected admin><AdminPage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </div>
  );
}

export default App;
